import type {
  DataAccessPrincipal,
  DataAccessSchema,
  DataAccessSource,
  DataAccessSummary,
  DataAccessTable,
} from '@ariksa/inventory-core';
import { INIT_PAGE_INFO } from 'services/utils/constants';

import type { PageInfo } from 'components/Navigation';

/** The two checkboxes above the table; both apply at every level. */
export interface DataAccessFilters {
  highPrivilegeOnly: boolean;
  sensitiveOnly: boolean;
}

/**
 * What the tab shows: the filters, the open rows and the page of each list.
 * A schema's or table list's page is kept by its parent's uuid.
 */
export interface DataAccessViewState {
  identity: string;
  filters: DataAccessFilters;
  databasePage: PageInfo;
  schemaPages: Record<string, PageInfo>;
  tablePages: Record<string, PageInfo>;
  openDatabases: string[];
  openSchemas: string[];
  // One table's details at a time.
  openTable: string | null;
}

export type DataAccessViewAction =
  | { type: 'reset'; identity: string }
  | { type: 'setFilters'; filters: Partial<DataAccessFilters> }
  | { type: 'resetPages' }
  | { type: 'setDatabasePage'; page: PageInfo }
  | { type: 'setSchemaPage'; databaseUuid: string; page: PageInfo }
  | { type: 'setTablePage'; schemaUuid: string; page: PageInfo }
  | { type: 'toggleDatabase'; uuid: string }
  | { type: 'toggleSchema'; uuid: string }
  | { type: 'toggleTable'; uuid: string };

export const initialViewState = (identity: string): DataAccessViewState => ({
  identity,
  filters: { highPrivilegeOnly: false, sensitiveOnly: false },
  databasePage: INIT_PAGE_INFO,
  schemaPages: {},
  tablePages: {},
  openDatabases: [],
  openSchemas: [],
  openTable: null,
});

const toggle = (list: string[], uuid: string) =>
  list.includes(uuid) ? list.filter(o => o !== uuid) : [...list, uuid];

const samePage = (a: PageInfo | undefined, b: PageInfo) =>
  a?.page_number === b.page_number && a?.page_size === b.page_size;

// Every list goes back to its first page; open rows stay open.
const firstPages = (state: DataAccessViewState): DataAccessViewState => ({
  ...state,
  databasePage: { ...state.databasePage, page_number: 1 },
  schemaPages: {},
  tablePages: {},
});

/**
 * Changing a filter, or a new rollup (`computed_at`), sends every list back
 * to page 1. The pager reports its page again on every render, so an
 * unchanged page keeps the same state.
 */
export const viewReducer = (
  state: DataAccessViewState,
  action: DataAccessViewAction,
): DataAccessViewState => {
  switch (action.type) {
    case 'reset':
      return initialViewState(action.identity);
    case 'setFilters': {
      const filters = { ...state.filters, ...action.filters };
      if (
        filters.highPrivilegeOnly === state.filters.highPrivilegeOnly &&
        filters.sensitiveOnly === state.filters.sensitiveOnly
      )
        return state;
      return firstPages({ ...state, filters });
    }
    case 'resetPages':
      return firstPages(state);
    case 'setDatabasePage':
      return samePage(state.databasePage, action.page)
        ? state
        : { ...state, databasePage: action.page };
    case 'setSchemaPage':
      return samePage(state.schemaPages[action.databaseUuid], action.page)
        ? state
        : {
            ...state,
            schemaPages: {
              ...state.schemaPages,
              [action.databaseUuid]: action.page,
            },
          };
    case 'setTablePage':
      return samePage(state.tablePages[action.schemaUuid], action.page)
        ? state
        : {
            ...state,
            tablePages: {
              ...state.tablePages,
              [action.schemaUuid]: action.page,
            },
          };
    case 'toggleDatabase':
      return {
        ...state,
        openDatabases: toggle(state.openDatabases, action.uuid),
      };
    case 'toggleSchema':
      return { ...state, openSchemas: toggle(state.openSchemas, action.uuid) };
    case 'toggleTable':
      return {
        ...state,
        openTable: state.openTable === action.uuid ? null : action.uuid,
      };
    default:
      return state;
  }
};

export interface SummaryCounts {
  databases: number;
  schemas: number;
  tables: number;
}

/** The header counts for the active filters. */
export const getSummaryCounts = (
  summary: DataAccessSummary | undefined,
  filters: DataAccessFilters,
): SummaryCounts => {
  const prefix =
    (filters.highPrivilegeOnly ? 'high_privilege_' : '') +
    (filters.sensitiveOnly ? 'sensitive_' : '');
  const count = (name: string): number =>
    (summary as Record<string, any> | undefined)?.[`${prefix}${name}_count`] ??
    0;
  return {
    databases: count('database'),
    schemas: count('schema'),
    tables: count('table'),
  };
};

const plural = (count: number, one: string, many: string) =>
  `${count.toLocaleString()} ${count === 1 ? one : many}`;

export const describeCounts = (counts: SummaryCounts) =>
  [
    plural(counts.databases, 'database', 'databases'),
    plural(counts.schemas, 'schema', 'schemas'),
    plural(counts.tables, 'table', 'tables'),
  ].join(' · ');

// The header buttons: each sets both filters at once.
export type FilterPreset = 'all' | 'high' | 'sensitive' | 'both';

export const FILTER_PRESETS: Record<FilterPreset, DataAccessFilters> = {
  all: { highPrivilegeOnly: false, sensitiveOnly: false },
  high: { highPrivilegeOnly: true, sensitiveOnly: false },
  sensitive: { highPrivilegeOnly: false, sensitiveOnly: true },
  both: { highPrivilegeOnly: true, sensitiveOnly: true },
};

const PRESET_LABELS: Record<FilterPreset, string> = {
  all: 'All',
  high: 'High privileges',
  sensitive: 'Sensitive',
  both: 'High & Sensitive',
};

const PRESETS = Object.keys(FILTER_PRESETS) as FilterPreset[];

/** The button that matches the filters; every pair has one. */
export const getFilterPreset = (filters: DataAccessFilters): FilterPreset =>
  PRESETS.find(
    preset =>
      FILTER_PRESETS[preset].highPrivilegeOnly === filters.highPrivilegeOnly &&
      FILTER_PRESETS[preset].sensitiveOnly === filters.sensitiveOnly,
  ) ?? 'all';

/**
 * One header button per filter pair, labelled with its database count, such
 * as "High privileges 40", with all three counts in its tooltip.
 */
export const getFilterPresetItems = (summary: DataAccessSummary | undefined) =>
  PRESETS.map(preset => {
    const counts = getSummaryCounts(summary, FILTER_PRESETS[preset]);
    return {
      value: preset,
      label: `${PRESET_LABELS[preset]} ${counts.databases.toLocaleString()}`,
      tooltip: describeCounts(counts),
    };
  });

// The levels the API counts as high privilege, in badge order.
export const HIGH_PRIVILEGE_LEVELS = [
  'Create',
  'Update',
  'Delete',
  'Permissions management',
  'Ownership',
  'Delegation',
];

export const getHighPrivilegeLevels = (levels: string[] = []) =>
  HIGH_PRIVILEGE_LEVELS.filter(level => levels.includes(level));

/** Who grants access: the first few roles, how many in all, and "Direct". */
export interface Grantors {
  roles: DataAccessPrincipal[];
  count: number;
  // The identity grants it itself.
  direct: boolean;
}

const toGrantors = (
  roles: DataAccessPrincipal[] = [],
  count = 0,
  direct = false,
): Grantors => ({ roles, count, direct });

/**
 * A cell's second line: "Direct" when the identity grants it itself, then the
 * first role, followed by a "+N" for the others (`more`). `compact` names the
 * number of roles instead, as "via 3 roles".
 */
export const describeGrantors = (grantors: Grantors, compact = false) => {
  const { roles, count, direct } = grantors;
  const first = roles[0]?.name ?? roles[0]?.uuid;
  const via = !first
    ? ''
    : compact && count > 1
    ? `via ${count} roles`
    : `via ${first}`;
  return {
    text: [direct ? 'Direct' : '', via].filter(Boolean).join(' · '),
    more: !compact && count > 1 ? count - 1 : 0,
  };
};

export type DataAccessRowKind = 'database' | 'schema' | 'table';

/** One row of the tree, whichever level it comes from. */
export interface DataAccessRow {
  kind: DataAccessRowKind;
  uuid: string;
  name: string;
  type: string;
  depth: number;
  // The grants on the resource itself. A table's are all of its grants.
  ownLevels: string[];
  ownGrantors: Grantors;
  // "40 tables · 4 schemas"; empty for a table.
  contains: string;
  // Who grants anything in or on the resource.
  grantors: Grantors;
  // Over the resource and everything inside it.
  levels: string[];
  sensitiveDataTypes: string[];
  sensitivityAvailable: boolean;
  isOpen: boolean;
  canOpen: boolean;
}

// A database or schema's own grants. The API doesn't say whether the identity
// itself is among them, so "Direct" shows only when no role grants them.
const ownGrantors = (container: DataAccessSource | DataAccessSchema) =>
  toGrantors(
    container.own_via_principals,
    container.own_via_principal_count,
    container.own_privilege_levels.length > 0 &&
      container.own_via_principal_count === 0,
  );

export const toDatabaseRow = (
  source: DataAccessSource,
  isOpen: boolean,
): DataAccessRow => ({
  kind: 'database',
  uuid: source.uuid,
  name: source.name ?? source.uuid,
  type: source.type,
  depth: 0,
  ownLevels: source.own_privilege_levels,
  ownGrantors: ownGrantors(source),
  contains: `${plural(source.table_count, 'table', 'tables')} · ${plural(
    source.schema_count,
    'schema',
    'schemas',
  )}`,
  grantors: toGrantors(
    source.via_principals,
    source.via_principal_count,
    source.has_direct_grant,
  ),
  levels: source.privilege_levels,
  sensitiveDataTypes: source.sensitive_data_types,
  sensitivityAvailable: true,
  isOpen,
  canOpen: source.schema_count > 0,
});

export const toSchemaRow = (
  schema: DataAccessSchema,
  isOpen: boolean,
  sensitivityAvailable: boolean,
): DataAccessRow => ({
  kind: 'schema',
  uuid: schema.uuid,
  name: schema.name ?? schema.uuid,
  type: schema.type,
  depth: 1,
  ownLevels: schema.own_privilege_levels,
  ownGrantors: ownGrantors(schema),
  contains: plural(schema.table_count, 'table', 'tables'),
  grantors: toGrantors(
    schema.via_principals,
    schema.via_principal_count,
    schema.has_direct_grant,
  ),
  levels: schema.privilege_levels,
  sensitiveDataTypes: schema.sensitive_data_types,
  sensitivityAvailable,
  isOpen,
  canOpen: schema.table_count > 0,
});

export const toTableRow = (
  table: DataAccessTable,
  isOpen: boolean,
  sensitivityAvailable: boolean,
): DataAccessRow => {
  const grantors = toGrantors(
    table.via_principals,
    table.via_principal_count,
    table.has_direct_grant,
  );
  return {
    kind: 'table',
    uuid: table.uuid,
    name: table.name ?? table.uuid,
    type: table.type,
    depth: 2,
    ownLevels: table.privilege_levels,
    ownGrantors: grantors,
    contains: '',
    grantors,
    levels: table.privilege_levels,
    sensitiveDataTypes: table.sensitive_data_types,
    sensitivityAvailable,
    isOpen,
    canOpen: true,
  };
};

/** A tag as "key: value", or its key alone when it has no value. */
export const formatTag = (key: string, value?: string | null) =>
  value === undefined || value === null || value === ''
    ? key
    : `${key}: ${value}`;

/** The API's reason for a failed request, or the fallback. */
export const getErrorMessage = (error: unknown, fallback: string): string =>
  (error as any)?.response?.data?.detail ?? fallback;
